import type {
  BuildUpdateStatus,
  DesignReconcileStatus,
} from '@volley/crucible-ipc-types/design-types';
import type { AutoBuildStatus } from '@volley/crucible-ipc-types/build-game-types';
import {
  hasBuiltRevision,
  savedPlanDiffersFromBuilt,
  type PlanBuiltComparison,
} from '../../components/plan-state';

/**
 * Pure derivations for the redesigned Design tab. One input snapshot drives
 * the nav Build Plan chip, the pinned nav-footer line, and the right-rail
 * Build card so every surface agrees on the workflow state.
 */
export interface DesignStatusInputs extends PlanBuiltComparison {
  /** A non-empty build plan markdown exists. */
  hasPlan: boolean;
  /** A build is currently running (plan locked). */
  buildRunning: boolean;
  /** Interview answers exist (legacy Fast plan-review games). */
  hasInterviewAnswers: boolean;
  /** Build-first automatic docs/package/build pipeline state. */
  autoBuildStatus?: AutoBuildStatus | null;
  /** From design:getBuildUpdateStatus; null while loading. */
  buildStatus: BuildUpdateStatus | null;
  /** From design:getReconcileStatus; null while loading. */
  reconcileStatus: DesignReconcileStatus | null;
}

export type StatusTone = 'gray' | 'blue' | 'amber' | 'green';

export interface BuildPlanChip {
  state: 'no-plan' | 'ready' | 'running' | 'built' | 'plan-pending' | 'auto-prep' | 'auto-error';
  label: string;
  tone: StatusTone;
  pulse: boolean;
}

function isAutoBuildPrep(status: AutoBuildStatus | null | undefined): boolean {
  return (
    status === 'armed' ||
    status === 'generating-docs' ||
    status === 'exporting-package' ||
    status === 'commit-starting'
  );
}

function isAutoBuildAttention(status: AutoBuildStatus | null | undefined): boolean {
  return status === 'failed' || status === 'cancelled';
}

export function planChangesPending(inputs: DesignStatusInputs): boolean {
  return hasBuiltRevision(inputs) && savedPlanDiffersFromBuilt(inputs);
}

export function designChangesPending(inputs: DesignStatusInputs): boolean {
  return (inputs.buildStatus?.hasSnapshot ?? false) && (inputs.buildStatus?.hasChanges ?? false);
}

/**
 * Derives reverse Build activity without collapsing loading/error to zero.
 *
 * Args:
 *   inputs: The current design workflow status snapshot.
 *
 * Returns:
 *   A known unread count, 0 for known-empty/no snapshot, or null when the
 *   snapshot exists but reconcile status is still unknown.
 *
 * Raises:
 *   None.
 */
function reverseBuildActivityCount(inputs: DesignStatusInputs): number | null {
  if (inputs.buildStatus?.hasSnapshot !== true) return 0;
  if (inputs.reconcileStatus === null) {
    // Unknown Build activity must block send actions until the reverse check resolves.
    return null;
  }
  const count = inputs.reconcileStatus.newMessageCount;
  return count > 0 ? count : 0;
}

/** The single status chip on the nav's Build Plan card. */
export function deriveBuildPlanChip(inputs: DesignStatusInputs): BuildPlanChip {
  if (isAutoBuildPrep(inputs.autoBuildStatus)) {
    return { state: 'auto-prep', label: 'Preparing build', tone: 'amber', pulse: true };
  }
  if (isAutoBuildAttention(inputs.autoBuildStatus)) {
    return { state: 'auto-error', label: 'Setup needs attention', tone: 'amber', pulse: false };
  }
  if (inputs.buildRunning) {
    return { state: 'running', label: 'Build running', tone: 'amber', pulse: true };
  }
  if (planChangesPending(inputs)) {
    return { state: 'plan-pending', label: 'Plan changes pending', tone: 'amber', pulse: false };
  }
  if (hasBuiltRevision(inputs)) {
    return { state: 'built', label: 'Built from this plan', tone: 'green', pulse: false };
  }
  if (!inputs.hasPlan) {
    return { state: 'no-plan', label: 'No plan yet', tone: 'gray', pulse: false };
  }
  return { state: 'ready', label: 'Ready to build', tone: 'blue', pulse: false };
}

export interface NavFooterLine {
  kind: 'hint' | 'running' | 'in-sync' | 'pending';
  text: string;
  tone: StatusTone;
  /** Hints navigate to the Build Plan view when clicked. */
  clickable: boolean;
}

/** Exactly one quiet line in the pinned nav footer per state. */
export function deriveNavFooterLine(inputs: DesignStatusInputs): NavFooterLine {
  if (isAutoBuildPrep(inputs.autoBuildStatus)) {
    return { kind: 'running', text: 'Preparing build…', tone: 'amber', clickable: false };
  }
  if (isAutoBuildAttention(inputs.autoBuildStatus)) {
    return {
      kind: 'pending',
      text: 'Build setup needs attention',
      tone: 'amber',
      clickable: false,
    };
  }
  if (inputs.buildRunning) {
    return { kind: 'running', text: 'Build running…', tone: 'amber', clickable: false };
  }
  const buildActivityCount = reverseBuildActivityCount(inputs);
  if (buildActivityCount === null && designChangesPending(inputs)) {
    return {
      kind: 'pending',
      text: 'Checking Build activity before sending design changes',
      tone: 'amber',
      clickable: false,
    };
  }
  if (buildActivityCount !== null && buildActivityCount > 0) {
    const messageWord = buildActivityCount === 1 ? 'message' : 'messages';

    // Build-originated updates need review before sending local design changes.
    return {
      kind: 'pending',
      text: `${buildActivityCount} new Build ${messageWord} to review`,
      tone: 'amber',
      clickable: false,
    };
  }
  if (designChangesPending(inputs)) {
    const n = inputs.buildStatus?.changedCount ?? 0;
    return {
      kind: 'pending',
      text: `${n} design change${n === 1 ? '' : 's'} to send`,
      tone: 'amber',
      clickable: false,
    };
  }
  if (planChangesPending(inputs)) {
    return {
      kind: 'hint',
      text: 'Plan changes pending — review',
      tone: 'amber',
      clickable: true,
    };
  }
  if (hasBuiltRevision(inputs) || inputs.buildStatus?.hasSnapshot === true) {
    const rev = inputs.lastBuiltPlanRevision;

    // Build-snapshot-only handoffs are still synced once pending work has cleared.
    return {
      kind: 'in-sync',
      text: rev === undefined ? 'In sync with the last build' : `In sync with build (rev ${rev})`,
      tone: 'green',
      clickable: false,
    };
  }
  return {
    kind: 'hint',
    text: inputs.hasPlan ? 'Next: review your Build Plan' : 'Next: draft your Build Plan',
    tone: 'gray',
    clickable: true,
  };
}

export interface BuildCardModel {
  state: 'none' | 'prep' | 'running' | 'built';
  /** Monospace header detail, e.g. "rev 4". */
  headDetail: string;
  /** Auto-build setup line before the build starts, or when setup needs retry. */
  prepLine?: string;
  /** Green built line, e.g. "Built · rev 4 · 2:14 PM". */
  builtLine?: string;
  /** Amber warning line (plan or design changes pending). */
  warnLine?: string;
  /** Amber queued line while running, e.g. "2 doc edits queued for after the build". */
  queuedLine?: string;
  /** Single derived primary action for the Build card. */
  primaryAction: BuildCardPrimaryAction;
}

export type BuildCardPrimaryAction =
  | { kind: 'review-build-changes'; label: string; count: number }
  | { kind: 'send-design-changes'; label: string; count: number }
  | null;

function formatTime(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** The compact right-rail Build card (running checklist is composed separately). */
export function deriveBuildCardModel(inputs: DesignStatusInputs): BuildCardModel {
  const n = inputs.buildStatus?.changedCount ?? 0;
  const rev = inputs.lastBuiltPlanRevision ?? inputs.planRevision;
  const headDetail = rev === undefined ? '' : `rev ${rev}`;
  const hasBuildSnapshot = inputs.buildStatus?.hasSnapshot === true;
  if (isAutoBuildPrep(inputs.autoBuildStatus)) {
    const prepLine =
      inputs.autoBuildStatus === 'commit-starting'
        ? 'Starting build…'
        : 'Generating design docs and build plan…';
    return {
      state: 'prep',
      headDetail: '',
      prepLine,
      primaryAction: null,
    };
  }
  if (isAutoBuildAttention(inputs.autoBuildStatus)) {
    return {
      state: 'prep',
      headDetail: '',
      prepLine:
        inputs.autoBuildStatus === 'cancelled'
          ? 'Build setup was cancelled. Retry from Build.'
          : 'Build setup failed. Retry from Build.',
      primaryAction: null,
    };
  }
  if (inputs.buildRunning) {
    return {
      state: 'running',
      headDetail: inputs.planRevision === undefined ? '' : `rev ${inputs.planRevision}`,
      queuedLine:
        n > 0 ? `${n} doc edit${n === 1 ? '' : 's'} queued for after the build` : undefined,
      primaryAction: null,
    };
  }
  // Build-first snapshots can carry reverse-sync activity before plan-built metadata exists.
  if (!hasBuiltRevision(inputs) && !hasBuildSnapshot) {
    return {
      state: 'none',
      headDetail: '',
      primaryAction: null,
    };
  }
  const time = formatTime(inputs.buildStatus?.lastSentAt);
  const builtLine = ['Built', headDetail, time].filter(Boolean).join(' · ');
  const designPending = designChangesPending(inputs);
  const planPending = planChangesPending(inputs);
  const buildActivityCount = reverseBuildActivityCount(inputs);
  const hasUnknownBuildActivity = buildActivityCount === null;
  const hasReverseReview = buildActivityCount !== null && buildActivityCount > 0;
  const buildMessageWord = buildActivityCount === 1 ? 'message' : 'messages';
  const warnLine = hasReverseReview
    ? `${buildActivityCount} new Build ${buildMessageWord} since the last design review.`
    : hasUnknownBuildActivity && designPending
      ? 'Checking Build activity before sending design changes.'
      : planPending
        ? `Plan revision ${inputs.planRevision ?? '—'} hasn't been built.`
        : designPending
          ? `${n} design change${n === 1 ? '' : 's'} not sent to the build.`
          : undefined;
  const primaryAction: BuildCardPrimaryAction = hasReverseReview
    ? {
        kind: 'review-build-changes',
        label: 'Review Build activity',
        count: buildActivityCount,
      }
    : designPending && !hasUnknownBuildActivity
      ? { kind: 'send-design-changes', label: `Send design changes (${n})`, count: n }
      : null;
  return {
    state: 'built',
    headDetail,
    builtLine,
    warnLine,
    primaryAction,
  };
}

export interface ScreensGenerateState {
  state: 'idle' | 'stale' | 'busy';
  label: string;
  title: string;
}

/** The Screens-group "Generate from design" button states. */
export function deriveScreensGenerateState(opts: {
  stale: boolean;
  busy: boolean;
}): ScreensGenerateState {
  if (opts.busy) {
    return {
      state: 'busy',
      label: 'Generating…',
      title: 'Generating screens from the design',
    };
  }
  if (opts.stale) {
    return {
      state: 'stale',
      label: 'Generate from design',
      title: 'Design docs changed since screens were generated — regenerate to pick up the changes',
    };
  }
  return {
    state: 'idle',
    label: 'Generate from design',
    title: 'Create / update screens automatically from the design',
  };
}
